// src/components/reuseable/StatDealsModal.jsx
// Drill-down popup for a dashboard money tile (Pending, Paid, Paid to Broker,
// Admin Fee YTD). Lists the deals behind that number — each row shows the deal,
// its stage + closing date, and its dollar share of that total. Clicking a row
// opens the deal's full page.
import { X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { formatCurrency } from "../../utils/helper";
import { parseLocalDate } from "../../utils/statusSheetData";

const dealLabel = (d) => {
  if (d?.propertyaddress) return d.propertyaddress;
  const names = Array.isArray(d?.customers)
    ? d.customers
        .map((c) => c?.clientName || c?.name)
        .filter(Boolean)
        .join(", ")
    : "";
  return names || "Untitled Deal";
};

const fmtDate = (s) => {
  // Central-safe: calendar dates parse as local, not UTC (UTC shows the
  // previous day for CST users).
  const dt = parseLocalDate(s);
  if (!dt) return "";
  return dt.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export default function StatDealsModal({ title, deals, amountOf, onClose }) {
  const navigate = useNavigate();
  const list = Array.isArray(deals) ? deals : [];
  const amt = (d) => Number(amountOf ? amountOf(d) : 0) || 0;
  const total = list.reduce((s, d) => s + amt(d), 0);

  const openDeal = (d) => {
    const id = d?.createdat || d?.id;
    if (!id) return;
    onClose?.();
    navigate(`/dashboard/deals-detail/${id}`);
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="rounded-2xl overflow-hidden shadow-2xl w-full max-w-lg max-h-[85vh] bg-white relative flex flex-col">
          <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-black/10 bg-[#1E293B] text-white flex-shrink-0">
            <div className="min-w-0">
              <h2 className="text-lg font-semibold truncate">{title}</h2>
              <p className="text-white/60 text-xs mt-0.5">
                {list.length} {list.length === 1 ? "deal" : "deals"} ·{" "}
                {formatCurrency(total)}
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-9 h-9 bg-white/15 hover:bg-white/25 rounded-full flex items-center justify-center transition-all hover:rotate-90 flex-shrink-0"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
            {list.length === 0 ? (
              <div className="py-14 text-center text-gray-400 text-sm">
                No deals here yet.
              </div>
            ) : (
              list.map((d, i) => (
                <button
                  key={d?.createdat || d?.id || i}
                  type="button"
                  onClick={() => openDeal(d)}
                  className="w-full flex items-center justify-between gap-3 px-5 py-3 text-left hover:bg-gray-50 transition"
                >
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-900 truncate">
                      {dealLabel(d)}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5 truncate">
                      {d?.dealStage || "—"}
                      {fmtDate(d?.closingdate)
                        ? ` · closes ${fmtDate(d.closingdate)}`
                        : ""}
                    </div>
                  </div>
                  <div className="text-sm font-bold text-slate-800 flex-shrink-0">
                    {formatCurrency(amt(d))}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      </div>
    </>
  );
}
